import * as _ from 'lodash';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AdminScopes, AdminUser, UserService } from '@app/shared/services/user.service';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { HelpersService } from '@app/shared/services/helpers.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { DateTime } from 'luxon';
import { AccountTypesForOTP, ShippingProviders } from '@app/shared/constants';
import { PageEvent } from '@angular/material/paginator';
import { Observable, Subscription } from 'rxjs';
import { Color, ScaleType } from '@swimlane/ngx-charts';
import { StateService, TimeBucketInfoInterface } from '@app/shared/services/state.service';
import { ViewportScroller } from '@angular/common';
import { ServiceTypeMetricsResponse, ServiceTypePerformanceResponse, TrendDirection, } from '../../otp.interfaces';
import { OtpService } from '@app/shared/services/otp.service';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';

@Component({
  standalone: false,
  selector: 'app-otp-services',
  templateUrl: './otp-services.component.html',
  styleUrls: ['./otp-services.component.scss'],
})
export class OtpServicesComponent implements OnInit, OnDestroy {
  // State management
  public timeBucket$ = this.stateService.timeBucketInfo$;
  private timeBucketSubscription: Subscription;
  public courierFilter$ = this.stateService.courierFilter$;
  private courierFilterSubscription: Subscription;
  public adminUserInfo$: Observable<any> = this.stateService.adminUserInfo$;
  private adminUserInfoSubscription: Subscription;

  // User permissions
  public canViewOtp: boolean = false;

  // Component state
  public loading: boolean = true;
  public error: string = '';
  public form: FormGroup | undefined;
  public currentPage: number = 0;
  public itemsPerPage: number = 10;
  public itemsPerPageOptions: number[] = [5, 10, 25, 50];

  // Filter state
  public currentBucket: string;
  public currentHistory: number;
  public accountType: AccountTypesForOTP;
  public courierFilter: ShippingProviders[];
  public selectedServiceTypes: string[] = [];
  public serviceTypesDropdown = [];

  // Add courier dropdown properties
  public couriersDropdown = [];
  public selectedCouriers: ShippingProviders[] = [];

  // Toggle for showing claim stats
  public showClaimsView: boolean = false;
  public hasClaimsData: boolean = false;

  // Date range form
  public daterange = {
    start: DateTime.now().minus({ day: 7 }).toJSDate(),
    end: DateTime.now().endOf('day').toJSDate(),
  };
  public range = new FormGroup({
    start: new FormControl<Date | null>(this.daterange.start),
    end: new FormControl<Date | null>(this.daterange.end),
  });

  // API response data
  public serviceTypeData: ServiceTypePerformanceResponse;

  // Summary metrics
  public totalServices: number = 0;
  public totalShipments: number = 0;
  public onTimeRate: number = 0;
  public averageDelay: number = 0;
  public guaranteedServicesCount: number = 0;
  public nonGuaranteedServicesCount: number = 0;

  // Claims metrics
  public totalClaims: number = 0;
  public overallClaimRate: number = 0;
  public claimRateTrend: TrendDirection = TrendDirection.STABLE;
  public guaranteedServiceClaims: number = 0;
  public guaranteedServiceClaimRate: number = 0;

  // Services data for visualizations
  public serviceTypeMetrics: ServiceTypeMetricsResponse[] = [];
  public serviceBreakdown = [];
  public servicePerformanceData = [];
  public serviceVolumeData = [];
  public serviceClaimRateData = [];
  public guaranteedVsNonGuaranteedData = [
    { name: 'Guaranteed', value: 0 },
    { name: 'Non-Guaranteed', value: 0 },
  ];
  public serviceTrendData = [];
  public serviceClaimsTrendData = [];

  public displayedDetailedMetrics: any[] = [];
  public allServiceMetrics: Array<{
    name: string;
    totalShipments: number;
    onTimePercentage: number;
    averageDelay: number;
    trend: TrendDirection;
  }> = [];

  // Detailed metrics table
  public detailedMetrics = [];
  public displayedColumns: string[] = ['serviceType', 'courier', 'volume', 'onTimePercent', 'avgDelay', 'isGuaranteed'];

  // Claims-enabled columns
  public claimsEnabledColumns: string[] = [
    'serviceType',
    'courier',
    'volume',
    'onTimePercent',
    'avgDelay',
    'isGuaranteed',
    'claims',
    'claimRate',
  ];

  // Color schemes for charts
  public defaultColorScheme: Color = {
    name: 'defaultScheme',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#5AA454', '#A10A28', '#C7B42C', '#17a2b8', '#6f42c1'],
  };

  public performanceColorScheme: Color = {
    name: 'performanceScheme',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#4caf50', '#8bc34a', '#ffeb3b', '#ff9800', '#f44336'],
  };

  public claimsColorScheme: Color = {
    name: 'claimsScheme',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#FF9800', '#E91E63', '#9C27B0', '#3F51B5', '#2196F3'],
  };

  constructor(
    private formBuilder: FormBuilder,
    private helpersService: HelpersService,
    private stateService: StateService,
    private logger: LoggerService,
    private userService: UserService,
    private otpService: OtpService,
    private bannerNotificationsService: BannerNotificationsService,
    private viewportScroller: ViewportScroller
  ) {}

  ngOnInit() {
    this.loading = true;
    this.adminUserInfoSubscription = this.adminUserInfo$.subscribe((adminUserInfo: AdminUser) => {
      this.canViewOtp = this.userService.hasAuthScope(adminUserInfo, [AdminScopes.VIEW_OTP]);
      if (this.canViewOtp) {
        this.initializeComponent();
      }
    });
  }

  ngOnDestroy() {
    this.adminUserInfoSubscription?.unsubscribe();
    this.timeBucketSubscription?.unsubscribe();
    this.courierFilterSubscription?.unsubscribe();
  }

  private initializeComponent() {
    // Create filter form
    this.createForm();

    // Subscribe to time bucket changes
    this.timeBucketSubscription = this.timeBucket$.subscribe((timeBucketInfo: TimeBucketInfoInterface | null) => {
      if (timeBucketInfo) {
        this.currentBucket = timeBucketInfo.timeBucket;
        this.currentHistory = timeBucketInfo.history || 1;
        this.accountType = timeBucketInfo.accountType;
        this.loadServiceTypeData();
      }
    });

    // Subscribe to courier filter changes
    this.courierFilterSubscription = this.courierFilter$.subscribe((courierFilter: ShippingProviders[] | null) => {
      if (courierFilter) {
        this.courierFilter = courierFilter;
        this.selectedCouriers = courierFilter;
        if (this.form) {
          this.form.patchValue({ couriers: this.selectedCouriers }, { emitEvent: false });
        }
        if (this.currentBucket) {
          this.loadServiceTypeData();
        }
      }
    });
  }

  private createForm() {
    this.form = this.formBuilder.group({
      serviceTypes: [this.selectedServiceTypes, []],
      couriers: [this.selectedCouriers, []],
    });

    // Populate couriers dropdown
    const couriers = this.otpService.getSupportedCouriers();
    this.couriersDropdown = couriers.map((courier) => ({
      id: courier.id,
      text: courier.name,
      selected: this.selectedCouriers?.includes(courier.id) || false,
    }));

    // Subscribe to form changes
    this.form.valueChanges.subscribe((values) => {
      if (values.serviceTypes !== this.selectedServiceTypes) {
        this.selectedServiceTypes = values.serviceTypes;
        this.filterServiceTypeData();
      }

      // Handle courier selection changes
      if (values.couriers !== this.selectedCouriers) {
        this.selectedCouriers = values.couriers;
        this.stateService.setCourierFilterData(this.selectedCouriers);
      }
    });
  }

  private loadServiceTypeData() {
    this.loading = true;
    this.error = '';

    const params = {
      timeBucket: this.currentBucket,
      history: this.currentHistory,
      accountType: this.accountType,
    };

    // Add courier filter if available
    if (this.courierFilter?.length > 0) {
      _.extend(params, { couriers: this.courierFilter });
    }

    this.otpService.getServiceTypes(params).subscribe(
      (response) => {
        this.logger.log('GET Service Types', response);
        this.serviceTypeData = response;
        this.processServiceTypeData();
        this.updateServiceTypesDropdown();
        this.filterServiceTypeData();
        this.loading = false;
      },
      (error) => {
        this.logger.error('GET Service Types error', error);
        this.error = 'Failed to load service type data';
        this.loading = false;
      }
    );
  }

  private processServiceTypeData() {
    if (!this.serviceTypeData) return;

    // Extract metrics
    this.serviceTypeMetrics = this.serviceTypeData.metrics || [];

    // Process summary metrics
    this.processSummaryMetrics();

    // Process service breakdown
    this.processServiceBreakdown();

    // Process trends data
    this.processTrendsData();

    // Process claims data
    this.processClaimsData();

    // Process detailed metrics table
    this.processDetailedMetrics();

    // Update displayedColumns based on claims data presence
    if (this.hasClaimsData) {
      this.displayedColumns = this.claimsEnabledColumns;
    } else {
      this.displayedColumns = ['serviceType', 'courier', 'volume', 'onTimePercent', 'avgDelay', 'isGuaranteed'];
    }
  }

  private processSummaryMetrics() {
    // Calculate total services
    this.totalServices = this.serviceTypeMetrics.length;

    // Calculate guaranteed vs non-guaranteed counts
    this.guaranteedServicesCount = this.serviceTypeMetrics.filter((s) => {
      const latestData = s.data[s.data.length - 1];
      return latestData?.isGuaranteed === true;
    }).length;

    this.nonGuaranteedServicesCount = this.serviceTypeMetrics.filter((s) => {
      const latestData = s.data[s.data.length - 1];
      return latestData?.isGuaranteed === false;
    }).length;

    // Update guaranteed vs non-guaranteed chart data
    this.guaranteedVsNonGuaranteedData = [
      { name: 'Guaranteed', value: this.guaranteedServicesCount },
      { name: 'Non-Guaranteed', value: this.nonGuaranteedServicesCount },
    ];

    // Get totals data from the response
    const totalsData = this.serviceTypeData.totals?.data || [];
    if (totalsData.length > 0) {
      const latestTotals = totalsData[totalsData.length - 1];
      this.totalShipments = latestTotals.totalShipments || 0;
      this.onTimeRate = latestTotals.onTimePercentage || 0;
      this.averageDelay = latestTotals.averageDelay || 0;
    }
  }

  private processServiceBreakdown() {
    // Process service type breakdown for visualizations
    const serviceBreakdown = this.serviceTypeData.serviceTypeBreakdown || {};

    // Transform for charts - full data for internal use
    const allServicesData = Object.keys(serviceBreakdown)
      .map((key) => {
        const service = serviceBreakdown[key];
        const latestData = service.data[service.data.length - 1];

        return {
          name: service.serviceName,
          value: latestData.totalShipments || 0,
          extra: {
            onTimePercentage: latestData.onTimePercentage || 0,
            averageDelay: latestData.averageDelay || 0,
            trend: service.trend?.onTimePercentage || TrendDirection.STABLE,
          },
        };
      })
      .sort((a, b) => b.value - a.value);

    // Save all services for reference
    this.allServiceMetrics = allServicesData.map((service) => ({
      name: service.name,
      totalShipments: service.value,
      onTimePercentage: service.extra.onTimePercentage,
      averageDelay: service.extra.averageDelay,
      trend: service.extra.trend,
    }));

    // Only take top 10 for charts
    this.serviceBreakdown = allServicesData.slice(0, 10);

    // Prepare service performance data - top 10 only
    this.servicePerformanceData = Object.keys(serviceBreakdown)
      .map((key) => {
        const service = serviceBreakdown[key];
        const latestData = service.data[service.data.length - 1];

        return {
          name: service.serviceName,
          value: latestData.onTimePercentage || 0,
          extra: {
            totalShipments: latestData.totalShipments || 0,
            averageDelay: latestData.averageDelay || 0,
            trend: service.trend?.onTimePercentage || TrendDirection.STABLE,
          },
        };
      })
      .sort((a, b) => b.extra.totalShipments - a.extra.totalShipments) // Sort by volume first
      .slice(0, 10);
  }

  private processTrendsData() {
    // Process time series data for trends
    if (!this.serviceTypeData.metrics || this.serviceTypeData.metrics.length === 0) {
      return;
    }

    // Get top services by volume
    const topServices = this.serviceBreakdown.slice(0, 5).map((s) => s.name);

    // Create trend data for line charts
    this.serviceTrendData = this.serviceTypeMetrics
      .filter((service) => topServices.includes(service.serviceName))
      .map((service) => {
        return {
          name: service.serviceName,
          series: service.data.map((item) => ({
            name: item.timeBucket,
            value: item.onTimePercentage,
          })),
        };
      });

    // For volume data, transform to stacked bar chart format
    const timePoints = this.serviceTypeData.timeBuckets || [];
    this.serviceVolumeData = timePoints.map((timeBucket) => {
      return {
        name: timeBucket,
        series: this.serviceTypeMetrics
          .filter((service) => topServices.includes(service.serviceName))
          .map((service) => {
            const bucketData = service.data.find((d) => d.timeBucket === timeBucket);
            return {
              name: service.serviceName,
              value: bucketData ? bucketData.totalShipments : 0,
            };
          }),
      };
    });
  }

  private processClaimsData() {
    // Check if claims data is available
    this.hasClaimsData = this.serviceTypeMetrics.some((service) =>
      service.data.some((item) => item.claims && (item.claims.totalClaims > 0 || item.claims.claimRate > 0))
    );

    if (!this.hasClaimsData) {
      return;
    }

    // Update display columns to include claims
    this.claimsEnabledColumns = [
      'serviceType',
      'courier',
      'volume',
      'onTimePercent',
      'avgDelay',
      'isGuaranteed',
      'claims',
      'claimRate',
    ];

    // Process claims summary metrics
    const totalsData = this.serviceTypeData.totals?.data || [];
    if (totalsData.length > 0) {
      const latestTotals = totalsData[totalsData.length - 1];
      this.totalClaims = latestTotals.totalClaims || 0;
      this.overallClaimRate = latestTotals.claimRate || 0;
      this.claimRateTrend = this.serviceTypeData.totals?.trend?.claimRate || TrendDirection.STABLE;
    }

    // Process service claim rates for visualization
    this.serviceClaimRateData = this.serviceTypeMetrics
      .filter((service) => service.data.some((d) => d.claims && d.claims.claimRate > 0))
      .map((service) => {
        const latestData = service.data[service.data.length - 1];
        return {
          name: service.serviceName,
          value: latestData.claims?.claimRate || 0,
          extra: {
            totalClaims: latestData.claims?.totalClaims || 0,
            totalShipments: latestData.totalShipments || 0,
          },
        };
      })
      .sort((a, b) => b.value - a.value);

    // Process claims trend data
    const topClaimServices = this.serviceClaimRateData.slice(0, 5).map((s) => s.name);

    this.serviceClaimsTrendData = this.serviceTypeMetrics
      .filter((service) => topClaimServices.includes(service.serviceName))
      .map((service) => {
        return {
          name: service.serviceName,
          series: service.data
            .filter((item) => item.claims)
            .map((item) => ({
              name: item.timeBucket,
              value: item.claims?.claimRate || 0,
            })),
        };
      });

    // Calculate guaranteed service claims
    let guaranteedClaims = 0;
    let guaranteedShipments = 0;

    this.serviceTypeMetrics.forEach((service) => {
      service.data.forEach((item) => {
        if (item.isGuaranteed === true && item.claims) {
          guaranteedClaims += item.claims.totalClaims || 0;
          guaranteedShipments += item.totalShipments || 0;
        }
      });
    });

    this.guaranteedServiceClaims = guaranteedClaims;
    this.guaranteedServiceClaimRate = guaranteedShipments > 0 ? (guaranteedClaims / guaranteedShipments) * 100 : 0;
  }

  private processDetailedMetrics() {
    // Process data for detailed metrics table
    this.detailedMetrics = this.serviceTypeMetrics.map((service) => {
      // Get the latest data for the service
      const latestData = service.data[service.data.length - 1];

      // Basic metrics
      const metrics = {
        serviceType: service.serviceType,
        serviceName: service.serviceName,
        courier: this.helpersService.convertShipmentProviderToString(service.courier),
        courierCode: service.courier,
        volume: latestData.totalShipments || 0,
        onTimePercent: latestData.onTimePercentage || 0,
        onTimeShipments: latestData.onTimeShipments || 0,
        avgDelay: latestData.averageDelay || 0,
        isGuaranteed: latestData.isGuaranteed === true ? 'Yes' : latestData.isGuaranteed === false ? 'No' : 'N/A',
        trend: service.trend?.onTimePercentage || TrendDirection.STABLE,
      };

      // Add claims data if available
      if (latestData.claims) {
        Object.assign(metrics, {
          claims: latestData.claims.totalClaims || 0,
          claimRate: latestData.claims.claimRate || 0,
          guaranteedClaims: latestData.claims.guaranteedServiceClaims?.count || 0,
          guaranteedClaimRate: latestData.claims.guaranteedServiceClaims?.percentageOfGuaranteedShipments || 0,
          lateDeliveryClaims: latestData.claims.claimsForLateDeliveries?.count || 0,
          lateDeliveryClaimRate: latestData.claims.claimsForLateDeliveries?.percentageOfLateDeliveries || 0,
        });
      }

      this.updateDisplayedMetrics();

      return metrics;
    });

    // Sort by volume (descending)
    this.detailedMetrics.sort((a, b) => b.volume - a.volume);
  }

  private updateServiceTypesDropdown() {
    this.serviceTypesDropdown = this.serviceTypeMetrics.map((service) => ({
      id: service.serviceType,
      text: service.serviceName,
    }));

    // Update form with available service types
    this.form.patchValue(
      {
        serviceTypes: this.selectedServiceTypes,
      },
      { emitEvent: false }
    );
  }

  private filterServiceTypeData() {
    // If no filters selected, show all data
    if (!this.selectedServiceTypes || this.selectedServiceTypes.length === 0) {
      this.processServiceTypeData();
      return;
    }

    // Filter the metrics based on selected service types
    const filteredMetrics = this.serviceTypeMetrics.filter((service) =>
      this.selectedServiceTypes.includes(service.serviceType)
    );

    // Re-process with filtered data
    this.serviceTypeMetrics = filteredMetrics;
    this.processServiceBreakdown();
    this.processTrendsData();
    this.processDetailedMetrics();
  }

  private updateDisplayedMetrics(): void {
    const startIndex = this.currentPage * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;

    // Create and store the displayed metrics slice
    this.displayedDetailedMetrics = this.detailedMetrics.slice(startIndex, endIndex);
  }

  public pageChanged(pageEvent: PageEvent): void {
    this.itemsPerPage = pageEvent.pageSize;
    this.currentPage = pageEvent.pageIndex;

    this.updateDisplayedMetrics();
  }

  public refreshData(): void {
    this.loadServiceTypeData();
  }

  // Helper to format percentage ticks for y-axis
  public formatYAxisPercentageTicks(val: any): string {
    if (typeof val === 'number') {
      return val.toFixed(1) + '%';
    }
    return val;
  }

  // Helper to format number ticks for y-axis
  public formatYAxisNumberTicks(val: any): string {
    if (typeof val === 'number') {
      return val.toLocaleString();
    }
    return val;
  }

  // Helper to format small percentage ticks for claims rate
  public formatClaimRateTicks(val: any): string {
    if (typeof val === 'number') {
      return val.toFixed(2) + '%';
    }
    return val;
  }

  // Helper to get trend icon based on direction
  public getTrendIcon(trend: TrendDirection): string {
    switch (trend) {
      case TrendDirection.IMPROVING:
        return 'trending_up';
      case TrendDirection.DECLINING:
        return 'trending_down';
      case TrendDirection.STABLE:
      default:
        return 'trending_flat';
    }
  }

  // Helper to get trend class for claims (inverse metric - lower is better)
  public getClaimsTrendClass(trend: TrendDirection): string {
    switch (trend) {
      case TrendDirection.IMPROVING:
        return 'positive-trend';
      case TrendDirection.DECLINING:
        return 'negative-trend';
      default:
        return 'stable-trend';
    }
  }

  // Helper to get color for claim rate
  public getClaimRateColor(rate: number): string {
    if (rate === undefined || rate === null) return '#6c757d'; // Gray for N/A
    if (rate === 0) return '#6c757d'; // Gray for zero
    if (rate < 0.5) return '#28a745'; // Green for low
    if (rate < 1.0) return '#ffc107'; // Yellow for medium
    return '#dc3545'; // Red for high
  }

  // Helper to format claim rate for display
  public formatClaimRate(rate: number): string {
    if (rate === undefined || rate === null) return 'N/A';
    return rate.toFixed(2) + '%';
  }

  // Handle error states
  public handleError(): void {
    this.error = '';
    this.refreshData();
  }

  public scrollToTable(): void {
    // Use timeout to ensure UI is updated before scrolling
    setTimeout(() => {
      this.viewportScroller.scrollToAnchor('serviceComparisonTable');
    }, 100);
  }
}
